import React, { createContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../auth/use-auth';
import { saveStandortState, loadStandortState, loadSelectedStandortId, saveSelectedStandortId } from '../../lib/standortState';
import { setAktiverStandort } from '../../lib/simulationsdatum';

interface Standort {
  id: number;
  name: string;
}

interface StandortContextType {
  selectedStandortId: number | null;
  selectedStandort: Standort | null;
  standorte: Standort[];
  setSelectedStandortId: (id: number) => void;
  isLoading: boolean;
}

const StandortContext = createContext<StandortContextType | undefined>(undefined);

export const StandortProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStandortId, setSelectedStandortIdState] = useState<number | null>(null);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const previousStandortIdRef = useRef<number | null>(null);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    const loadStandorte = async () => {
      // Nur laden, wenn User eingeloggt ist
      if (!isAuthenticated || !user) {
        setStandorte([]);
        setSelectedStandortIdState(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const data = await api.get<Array<{ id: number; name: string }>>('/api/standorte');
        // Das Backend liefert nur die Standorte im Scope des Benutzers (RBAC) —
        // keine Filterung nach Rollen im Client.
        const filteredStandorte = data;
        setStandorte(filteredStandorte);
        const persistedStandortId = loadSelectedStandortId(user.id);
        
        // Wähle Standort aus: aktuell gesetzt > persistiert > User-Standort > erster Standort
        if (filteredStandorte.length > 0 && !selectedStandortId) {
          const persisted = persistedStandortId
            ? filteredStandorte.find(s => s.id === persistedStandortId)?.id
            : undefined;
          const preferred = user?.standortId ? filteredStandorte.find(s => s.id === user.standortId)?.id : undefined;
          setSelectedStandortIdState(persisted ?? preferred ?? filteredStandorte[0].id);
        } else if (filteredStandorte.length === 0) {
          setSelectedStandortIdState(null);
        } else if (selectedStandortId && !filteredStandorte.some(s => s.id === selectedStandortId)) {
          const persisted = persistedStandortId
            ? filteredStandorte.find(s => s.id === persistedStandortId)?.id
            : undefined;
          const preferred = user?.standortId ? filteredStandorte.find(s => s.id === user.standortId)?.id : undefined;
          setSelectedStandortIdState(persisted ?? preferred ?? filteredStandorte[0].id);
        }
      } catch (e) {
        console.warn('Standorte konnten nicht geladen werden', e);
        // Bei 401 oder anderen Auth-Fehlern leere Liste setzen
        setStandorte([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadStandorte();
  }, [isAuthenticated, user]);

  const selectedStandort = standorte.find(s => s.id === selectedStandortId) || null;

  // Speichere aktuelle Route, wenn sich der Standort ändert
  useEffect(() => {
    // Speichere Route des vorherigen Standorts, wenn wir wechseln
    if (previousStandortIdRef.current !== null && previousStandortIdRef.current !== selectedStandortId && !isNavigatingRef.current) {
      const previousPathname = location.pathname;
      const previousSearch = location.search;
      
      // Speichere nur, wenn wir nicht gerade navigieren (um Endlosschleifen zu vermeiden)
      saveStandortState(previousStandortIdRef.current, {
        pathname: previousPathname,
        search: previousSearch || undefined,
      });
    }

    // Lade gespeicherte Route für neuen Standort — nur bei einem echten Wechsel. Beim
    // ersten Laden (vorher kein Standort) gilt die aufgerufene URL, sonst würden
    // Deep-Links und Reloads auf die zuletzt besuchte Seite umgeleitet.
    if (
      selectedStandortId !== null &&
      previousStandortIdRef.current !== null &&
      previousStandortIdRef.current !== selectedStandortId &&
      !isNavigatingRef.current
    ) {
      const savedState = loadStandortState(selectedStandortId);
      
      if (savedState) {
        isNavigatingRef.current = true;
        const targetPath = savedState.pathname + (savedState.search || '');
        
        // Navigiere nur, wenn wir nicht bereits auf dieser Route sind
        if (location.pathname !== savedState.pathname || location.search !== (savedState.search || '')) {
          navigate(targetPath, { replace: true });
        }
        
        // Reset nach kurzer Verzögerung, um sicherzustellen, dass Navigation abgeschlossen ist
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    }

    previousStandortIdRef.current = selectedStandortId;
  }, [selectedStandortId, location.pathname, location.search, navigate]);

  // Speichere Route bei jeder Navigation (außer wenn wir gerade Standort wechseln)
  useEffect(() => {
    if (selectedStandortId !== null && !isNavigatingRef.current) {
      saveStandortState(selectedStandortId, {
        pathname: location.pathname,
        search: location.search || undefined,
      });
    }
  }, [location.pathname, location.search, selectedStandortId]);

  useEffect(() => {
    if (!user?.id || selectedStandortId === null) {
      return;
    }
    saveSelectedStandortId(user.id, selectedStandortId);
  }, [user?.id, selectedStandortId]);

  // Testsystem: der API-Client schickt den Standort mit (Simulationsdatum).
  useEffect(() => {
    setAktiverStandort(selectedStandortId);
  }, [selectedStandortId]);

  const setSelectedStandortId = (id: number) => {
    setSelectedStandortIdState(id);
  };

  return (
    <StandortContext.Provider value={{
      selectedStandortId,
      selectedStandort,
      standorte,
      setSelectedStandortId,
      isLoading
    }}>
      {children}
    </StandortContext.Provider>
  );
};

export { StandortContext };

